import type SignatureCanvasType from 'react-signature-canvas';

// With clearOnResize={false} a resize no longer wipes ink, but a WIDTH change
// (rotation, desktop window drag) leaves the canvas backing store at its mount
// size: the ink displays stretched and new strokes land offset. This attaches a
// ResizeObserver per canvas that snapshots the drawing, re-sizes the backing
// store for the new CSS size × devicePixelRatio, and restores the ink through
// signature_pad's fromDataURL — which also keeps its isEmpty() flag truthful
// (validation and staging read it). Height-only resizes (soft keyboard,
// URL-bar) never change the element's box, so the observer stays quiet there.
//
// v2-local: attach from the renderer; shared components stay untouched.

export function attachCanvasPreserve(
  components: Array<SignatureCanvasType | null | undefined>,
): () => void {
  const observers: ResizeObserver[] = [];
  for (const component of components) {
    if (!component) continue;
    let el: HTMLCanvasElement;
    try { el = component.getCanvas(); } catch { continue; }
    if (!el || !el.isConnected) continue;
    const ro = new ResizeObserver(() => {
      try {
        const cssW = el.offsetWidth;
        const cssH = el.offsetHeight;
        if (!cssW || !cssH) return;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        if (el.width === Math.round(cssW * ratio) && el.height === Math.round(cssH * ratio)) return;
        const snapshot = component.isEmpty() ? null : component.toDataURL('image/png');
        el.width = Math.round(cssW * ratio);
        el.height = Math.round(cssH * ratio);
        const ctx = el.getContext('2d');
        if (ctx) ctx.scale(ratio, ratio);
        if (snapshot) component.fromDataURL(snapshot, { width: cssW, height: cssH, ratio: 1 });
        else component.clear();
      } catch { /* a mid-unmount resize is not worth crashing the form */ }
    });
    ro.observe(el);
    observers.push(ro);
  }
  return () => observers.forEach((ro) => ro.disconnect());
}
